import React, { useEffect, useState } from 'react';
import { Alert, Grid, Stack } from '@mui/material';
import { DeleteForeverOutlined } from '@mui/icons-material';
import { Button, ButtonColorProps, ButtonVariantProps } from '../../../../../components/atoms/Button';
import { Typography, TypographyFontWeightProps, TypographyVariantProps } from '../../../../../components/atoms/Typography';
import { TextField, TextFieldTypeProps } from '../../../../../components/atoms/TextField';
import { ImagePopup, ImagePopupSlide } from '../../../../../components/atoms/ImagePopup';
import { PartTypeActionSearch } from '../../../../../components/molecules/PartTypeActionSearch';
import { PartTypeActionNote } from '../../../../../components/molecules/PartTypeActionNote';
import { PartTypeLocationItemData } from '../../../../../components/molecules/PartTypeLocationItem';
import { PartLocationSelectPopup } from '../../../../../components/organisms/PartLocationSelectPopup';
import { useGetUploadedImages } from '../../../../../queries/uploadedfiles-query';
import { CustomPermission, IconColorProps, PartTypeActionLiveSearchItem, PartTypeActionLiveSearchItemCategoryCode } from '../../../../../@types';
import { Folders } from '../../../../../@types/uploaded-files.type';
import { isEmptyString } from '../../../../../utils/common';
import { PartActionReplaceCardProps } from './PartActionReplaceCard.props';
import { 
  DefaultCard, 
  StyledCardContent,
  CardTitle,
  CloseWrapper,
  SelectButton,
  InnerWrapper,
  OuterWrapper
} from '../PartAction.styles';
import { useGetPartLocationDataByServiceId } from '../../../../../queries/part-query';
import { useGetUserSystemPermissions } from '../../../../../queries/user-query';
import { Action, PartActionType, PartActionValidateType } from '../../../../../@types/partAction.type';
import { partActionConditionValidation, partActionInValidations, triggerWarning } from '../../../../../utils/part-action';
import { useSubLocationsForCreateServices } from '../../../../../queries/live-search-query';
import { useCheckMatchingSKUConfigs } from '../../../../../queries/sku-config-query';
import { blockInteractionAvailable, getBlockInteractionMessage, getFormIsRepeatableStatus, validateInteractionForms } from '../../../../../utils/interactions';
import { COLORS } from '../../../../../configs/colors';
import { FormInterceptor } from '../FormInterceptor';
import { MerchantDamagePartSerialMenuData } from '../../../../../@types/part.type';
import { Loader } from '../../../../../components/atoms/Loader';

export const PartActionReplaceCard: React.FC<PartActionReplaceCardProps> = ({
  service,
  value,
  helperText,
  onChange,
  onClose,
  setHelperText,
  isMobileView,
  interceptedFormsObject,
  setInterceptedFormsObject,
  repeatableFormData,
  setRepeatableFormData,
  setMerchantDamageObjectParcer,
  partActionData,
  isInteractionSubmiting,
  submitInteractionForms
}) => {
  const [isOutSearchMode, setIsOutSearchMode] = useState(true);
  const [isInSearchMode, setIsInSearchMode] = useState(true);
  const [isInSerialized, setIsInSerialized] = useState(false);
  const [isOutSerialized, setIsOutSerialized] = useState(false);
  const [showOutSerialSearch, setShowOutSerialSearch] = useState(false);
  const [showPartType, setShowPartType] = useState(true);
  const [showInSerialSearch, setShowInSerialSearch] = useState(false);
  const [isSelectInLocationPopup, setIsSelectInLocationPopup] = useState(false);
  const [outPartImages, setOutPartImages] = useState<Array<ImagePopupSlide>>([]);
  const [inPartImages, setInPartImages] = useState<Array<ImagePopupSlide>>([]);
  const [permissions, setPermissions] = useState<CustomPermission[]>();
  const [siteHierarchyWarning, setSiteHierarchyWarning] = useState<string>('');
  const [underClientStructureWarning, setUnderClientStructureWarning] = useState<string>('');
  const [partTypeMismatchWarning, setPartTypeMismatchWarning] = useState<string>('');
  const [locationInteractionSubmitting, setLocationInteractionSubmitting] = useState(false);

  const outValueUploadedImagesQuery = useGetUploadedImages(value?.outValue?.partTypeId || 0, Folders.PartTypesImages);
  const inValueUploadedImagesQuery = useGetUploadedImages(value?.inValue?.partTypeId || 0, Folders.PartTypesImages);
  const inPartLocationDataByServiceIdQuery = useGetPartLocationDataByServiceId(value?.inValue?.partTypeId || 0, service.id);
  const subLocationsForCreateServicesQuery = useSubLocationsForCreateServices();
  const checkMatchingSKUConfigsQuery = useCheckMatchingSKUConfigs(value.value.id, value?.inValue?.partId || 0, value?.outValue?.partId || 0);

  const getUserPermissionsQuery = useGetUserSystemPermissions();

  useEffect(() => {
    getUserPermissionsQuery.data && setPermissions(getUserPermissionsQuery.data);
  }, [getUserPermissionsQuery.data]);

  useEffect(() => {
    const outValidations = async () => {
      if (value?.outValue?.isInSiteHierarchy === false && value?.outValue?.serial) {
        const subLocationsResult = await subLocationsForCreateServicesQuery.mutateAsync(value.outValue?.locationId);
        const main = subLocationsResult?.find(subLocation => subLocation.isMain);
    
        const partParentNode = main ? `${main.name} /` : '';
        const serviceBaseNode = service.subLocation ? `/ ${service.subLocation}` : '';

        setSiteHierarchyWarning(`Warning: ${value?.outValue?.serial} is not at ${service.mainLocation} ${serviceBaseNode}, but at ${partParentNode} ${value.outValue?.location}. This is an unexpected part location; please confirm the correct part has been selected prior to proceeding. installed in ${value?.outValue?.name}: ${value?.outValue?.serial}`);
      } else {
        setSiteHierarchyWarning('');
      } 
      
      if (value?.outValue?.isUnderClientStructure === false && value?.outValue?.serial) {
        const baseOrParentnode = service.subLocation ? service.subLocation : service.mainLocation;
        setUnderClientStructureWarning(`Warning: ${value?.outValue?.serial} is not at ${baseOrParentnode}, but at ${value.outValue?.location}.  Please confirm the correct part has been selected prior to proceeding.`);
      } else {
        setUnderClientStructureWarning('');
      }
    };

    outValidations();
  }, [value?.outValue?.isInSiteHierarchy, value?.outValue?.isUnderClientStructure, value?.outValue?.serial]);
  
  useEffect(() => {
    if (value?.inValue?.name === value?.outValue?.name && value?.outValue?.name !== '') {
      setShowPartType(false);
    }
    
    value?.outValue?.name && setIsOutSearchMode(isEmptyString(value?.outValue?.name));
    value?.inValue?.name && setIsInSearchMode(isEmptyString(value?.inValue?.name));
    
    setIsInSerialized(value?.inValue?.category === PartTypeActionLiveSearchItemCategoryCode.Serialised);
    setIsOutSerialized(value?.outValue?.category === PartTypeActionLiveSearchItemCategoryCode.Serialised );

    value?.inValue?.partTypeId && inValueUploadedImagesQuery.refetch();
    value?.outValue?.partTypeId && outValueUploadedImagesQuery.refetch();
  }, [value?.inValue?.name , value?.inValue?.category, value?.outValue?.name, value?.outValue?.category, value?.inValue?.partTypeId, value?.outValue?.partTypeId]);

  useEffect(() => {
    if (value?.outValue?.name != '' || value?.inValue?.name != '') {
      setShowOutSerialSearch(value?.outValue?.category === PartTypeActionLiveSearchItemCategoryCode.Serialised);
      setShowInSerialSearch(value?.inValue?.category === PartTypeActionLiveSearchItemCategoryCode.Serialised);  
    }
  }, [value?.outValue?.name, value?.inValue?.name]);
 
  useEffect(() => {
    if (value.inValue?.partTypeId !== value.outValue?.partTypeId && value?.outValue?.partId && value?.inValue?.partId) {
      checkMatchingSKUConfigsQuery.refetch();
    }
    setPartTypeMismatchWarning('');
  }, [value.inValue?.id, value.outValue?.id, value.value.id]);

  useEffect(() => {
    if (value.inValue?.partTypeId !== value.outValue?.partTypeId && !checkMatchingSKUConfigsQuery.data) {
      setPartTypeMismatchWarning(triggerWarning(PartActionType.REPLACE, {
        in: {
          serial1: value.inValue?.serial,
          partTypeName: value.inValue?.name
        },
        out: {
          serial1: value.outValue?.serial,
          partTypeName: value.outValue?.name
        }
      }) ?? '');
    } else {
      setPartTypeMismatchWarning('');
    }
  }, [checkMatchingSKUConfigsQuery.data]);

  useEffect(() => {
    if (outValueUploadedImagesQuery.data) {
      const outImages: Array<ImagePopupSlide> = outValueUploadedImagesQuery.data && outValueUploadedImagesQuery.data.map((obj) => ({
        url: obj.url
      }));
      setOutPartImages(outImages);
    }
    if (inValueUploadedImagesQuery.data) {
      const inImages: Array<ImagePopupSlide> = inValueUploadedImagesQuery.data && inValueUploadedImagesQuery.data.map((obj) => ({
        url: obj.url
      }));
      setInPartImages(inImages);
    }

  }, [outValueUploadedImagesQuery.data, inValueUploadedImagesQuery.data]);

  const handleOutPartTypeActionSearch = async (liveSearchSelectedItem: PartTypeActionLiveSearchItem) => {
    if (liveSearchSelectedItem?.isPartCreation) {
      onChange({
        newPartSerial: liveSearchSelectedItem?.name,
        isPartCreation: true,
        action: Action.OUT,
        partActionType: PartActionType.REPLACE,
        value: value.value,
        outValue: value.outValue,
        inValue: value.inValue
      });
    } else {
      setHelperText({
        ...helperText,
        out: {
          ...helperText.out,
          partTypeHelperText: '',
          partSerialHelperText: ''
        },
      });
      if (liveSearchSelectedItem?.serial1) {
        onChange({
          value: value.value,
          outValue: {
            ...value.outValue,
            id: value.outValue?.id || 0,
            partTypeId: liveSearchSelectedItem?.partTypeId,
            partId: liveSearchSelectedItem?.partId,
            name: liveSearchSelectedItem?.partTypeName,
            category: liveSearchSelectedItem?.categoryCode,
            location: liveSearchSelectedItem?.locationName,
            locationId: liveSearchSelectedItem?.locationId,
            condition: liveSearchSelectedItem?.conditionCode,
            conditionName: liveSearchSelectedItem?.partCondition?.name,
            serial: liveSearchSelectedItem?.serial1,
            sw: liveSearchSelectedItem?.softwareVersion,
            hw: liveSearchSelectedItem?.hardwareVersion,
            fw: liveSearchSelectedItem?.firmwareVersion,
            ft: liveSearchSelectedItem?.fleetTagName,
            isInSiteHierarchy: liveSearchSelectedItem?.isInSiteHierarchy,
            isUnderClientStructure: liveSearchSelectedItem?.isUnderClientStructure,
            isAccepted: true
          },
          inValue: value.inValue
        });
      } else {
        onChange({
          value: value.value,
          outValue: {
            ...value.outValue,
            id: value.outValue?.id || 0,
            partTypeId: liveSearchSelectedItem?.partTypeId,
            ns_partId: liveSearchSelectedItem?.partId,
            name: liveSearchSelectedItem?.partTypeName,
            category: liveSearchSelectedItem?.categoryCode,
            location: liveSearchSelectedItem?.locationName,
            condition: liveSearchSelectedItem?.conditionCode,
            conditionName: liveSearchSelectedItem?.partCondition?.name,
            serial: '',
            sw: liveSearchSelectedItem?.softwareVersion,
            hw: liveSearchSelectedItem?.hardwareVersion,
            fw: liveSearchSelectedItem?.firmwareVersion,
            ft: liveSearchSelectedItem?.fleetTagName,
            isAccepted: true
          },
          inValue: value.inValue
        });
      }
    }
  };

  const handleInPartTypeActionSearch = async (liveSearchSelectedItem: PartTypeActionLiveSearchItem) => {
    if (liveSearchSelectedItem?.isPartCreation) {
      onChange({
        newPartSerial: liveSearchSelectedItem?.name,
        isPartCreation: true,
        action: Action.IN,
        partActionType: PartActionType.REPLACE,
        value: value.value,
        outValue: value.outValue,
        inValue: value.inValue
      });
    } else {
      setHelperText({
        ...helperText,
        in: {
          ...helperText.in,
          partTypeHelperText: '',
          partSerialHelperText: ''
        },
      });

      if (liveSearchSelectedItem?.serial1) {
        onChange({
          value: value.value,
          inValue: {
            ...value.inValue,
            id: value.inValue?.id || 0,
            partTypeId: liveSearchSelectedItem?.partTypeId,
            partId: liveSearchSelectedItem?.partId,
            name: liveSearchSelectedItem?.partTypeName,
            category: liveSearchSelectedItem?.categoryCode,
            location: liveSearchSelectedItem?.locationName,
            condition: liveSearchSelectedItem?.conditionCode,
            conditionName: liveSearchSelectedItem?.partCondition?.name,
            serial: liveSearchSelectedItem?.serial1,
            sw: liveSearchSelectedItem?.softwareVersion,
            hw: liveSearchSelectedItem?.hardwareVersion,
            fw: liveSearchSelectedItem?.firmwareVersion,
            ft: liveSearchSelectedItem?.fleetTagName,
            serviceId: liveSearchSelectedItem?.serviceId,
            isInSiblingHierarchy: liveSearchSelectedItem?.isInSiblingHierarchy,
            isAccepted: true
          },
          outValue: value.outValue
        });
      } else {
        onChange({
          value: value.value,
          inValue: {
            ...value.inValue,
            id: value.inValue?.id || 0,
            partTypeId: liveSearchSelectedItem?.partTypeId,
            ns_partId: liveSearchSelectedItem?.partId,
            name: liveSearchSelectedItem?.partTypeName,
            category: liveSearchSelectedItem?.categoryCode,
            condition: liveSearchSelectedItem?.conditionCode,
            conditionName: liveSearchSelectedItem?.partCondition?.name,
            location: liveSearchSelectedItem?.locationName,
            serial: '',
            sw: liveSearchSelectedItem?.softwareVersion,
            hw: liveSearchSelectedItem?.hardwareVersion,
            fw: liveSearchSelectedItem?.firmwareVersion,
            ft: liveSearchSelectedItem?.fleetTagName,
            isAccepted: true
          },
          outValue: value.outValue
        });
      }
    }
  };


  const handleOutPartTypeSerialSearch = (val: PartTypeActionLiveSearchItem) => {
    if (val.isPartCreation) {
      onChange({
        newPartSerial: val.name,
        isPartCreation: true,
        action: Action.OUT,
        partActionType: PartActionType.REPLACE,
        value: value.value,
        outValue: value.outValue,
        inValue: value.inValue
      });
    } else {
      setHelperText({
        ...helperText,
        out: {
          ...helperText.out,
          partTypeHelperText: '',
          partSerialHelperText: ''
        },
      });

      const updatedValue = {
        value: value.value,
        outValue: {
          ...value.outValue,
          id: value.outValue?.id || 0,
          ns_partId: NaN,
          partId: val.partId,
          location: val.locationName,
          locationId: val.locationId,
          serial: val.serial1,
          condition: val.conditionCode,
          conditionName: val?.partCondition?.name,
          sw: val.softwareVersion,
          hw: val.hardwareVersion,
          fw: val.firmwareVersion,
          ft: val.fleetTagName,
          isInSiteHierarchy: val.isInSiteHierarchy,
          isUnderClientStructure: val.isUnderClientStructure
        },
        inValue: value.inValue
      };

      onChange(updatedValue);
    }
  };

  const handleInPartTypeSerialSearch = (val: PartTypeActionLiveSearchItem) => {
    if (val.isPartCreation) {
      onChange({
        newPartSerial: val.name,
        isPartCreation: true,
        action: Action.OUT,
        partActionType: PartActionType.REPLACE,
        value: value.value,
        outValue: value.outValue,
        inValue: value.inValue
      });
    } else {
      setHelperText({
        ...helperText,
        in: {
          ...helperText.in,
          partTypeHelperText: '',
          partSerialHelperText: ''
        },
      });
      const updatedValue = {
        value: value.value,
        inValue: {
          ...value.inValue,
          id: value.inValue?.id || 0,
          ns_partId: NaN,
          partId: val.partId,
          location: val.locationName,
          serial: val.serial1,
          condition: val.conditionCode,
          conditionName: val?.partCondition?.name,
          sw: val.softwareVersion,
          hw: val.hardwareVersion,
          fw: val.firmwareVersion,
          ft: val.fleetTagName,
          serviceId: val.serviceId,
          isInSiblingHierarchy: val.isInSiblingHierarchy
        },
        outValue: value.outValue
      };

      onChange(updatedValue);
    }
  };

  const handleInPartTypeLocationSelect = (val: PartTypeLocationItemData) => {
    setHelperText({
      ...helperText,
      in: { 
        ...helperText.in,
        locationHelperText: ''
      },
    });
    const updatedValue = {
      value: value.value,
      inValue: {
        ...value.inValue,
        id: value.inValue?.id || 0,
        partId: NaN,
        serial: '',
        ns_partId: val.ns_partId,
        location: val.locationName,
        condition: val.conditionCode,
        conditionName: val?.partCondition?.name,
        ft: val.fleetTagName
      },
      outValue: value.outValue
    };

    onChange(updatedValue);
    setIsSelectInLocationPopup(false);
  };

  const submitInteractions = async() => {
    if (interceptedFormsObject?.forms && interceptedFormsObject?.forms?.length > 0) {
      const { hasErrorFields, hasByPassedFields, blockInteractionBypassed, updatedForms } = validateInteractionForms(interceptedFormsObject, service, getUserPermissionsQuery.data || []);
      if (updatedForms?.length > 0 && (hasErrorFields || hasByPassedFields || blockInteractionBypassed)) {
        setInterceptedFormsObject({ ...interceptedFormsObject, forms: updatedForms });
      };
      if (hasErrorFields) {
        return;
      } else {
        const updatedFormsObject = { ...interceptedFormsObject, forms: updatedForms };
        setLocationInteractionSubmitting(true);
        await submitInteractionForms(updatedFormsObject);
        setLocationInteractionSubmitting(false);
      }
    }
  };

  return (
    <DefaultCard>
      <StyledCardContent isMobileView={isMobileView}>
        <CardTitle>Replace</CardTitle>
        {partActionData?.executionCompleted ?
          <Stack justifyContent="center" alignItems="center">
            <Typography variant={TypographyVariantProps.Subtitle1} textColor={COLORS.Gray}>
              Part Action Submitted
            </Typography>
          </Stack>
          : !interceptedFormsObject?.forms || !interceptedFormsObject?.forms?.length ?
            <OuterWrapper>
              <CloseWrapper onClick={onClose} isMobileView={isMobileView}>
                <DeleteForeverOutlined color={IconColorProps.Error} fontSize={isMobileView ? 'large' : 'medium'} />
              </CloseWrapper>
              <InnerWrapper isMobileView={isMobileView} spacing={1}>
                <Stack direction="row" justifyContent="space-between" spacing={1} width="100%" marginTop={1}>
                  <Stack width="20%">
                    <Typography
                      variant={TypographyVariantProps.Body2} 
                      fontWeight={TypographyFontWeightProps.Bold}
                    >
                Out
                    </Typography>
                  </Stack>
                  <Stack direction="column" spacing={2} width="100%">
                    <PartTypeActionSearch
                      title={showOutSerialSearch ? 'Part Type Search' : 'Part/Part Type Input'}
                      serviceData={service}
                      value={value?.outValue?.name || ''} 
                      partActionType={PartActionType.REPLACE}
                      action={Action.OUT}
                      error={!!helperText.out.partTypeHelperText}
                      helperText={helperText.out.partTypeHelperText}
                      locationId={service.subLocationNodeId || service.mainLocationNodeId}
                      onChange={handleOutPartTypeActionSearch}
                      searchType={showOutSerialSearch ? 'part-type' : 'part-and-part-type'}
                      IsPartCreationEnabled={showOutSerialSearch ? false : true}
                    />
                    {showOutSerialSearch &&
                <PartTypeActionSearch
                  title="Part Serial Search"
                  serialiseType={value?.outValue?.category}
                  partTypeId={`${value?.outValue?.partTypeId}`}
                  serviceData={service}
                  value={value?.outValue?.serial || ''}
                  partActionType={PartActionType.REPLACE}
                  action={Action.OUT}
                  error={!!helperText.out.partSerialHelperText}
                  helperText={helperText.out.partSerialHelperText}
                  locationId={service.subLocationNodeId || service.mainLocationNodeId}
                  onChange={handleOutPartTypeSerialSearch}
                  searchType="part"
                />
                    }
                  </Stack>
                  {isOutSearchMode || <ImagePopup title="Photos" images={outPartImages} />}
                </Stack>
                {isOutSearchMode || 
            <>
              {value?.outValue?.condition && value?.outValue?.serial && partActionConditionValidation(PartActionValidateType.REPLACEOUT, value.outValue.condition, value.outValue.serial) &&
                <Grid size={{ xs: 12 }}>
                  <Alert severity="warning">{partActionConditionValidation(PartActionValidateType.REPLACEOUT, value.outValue.condition, value.outValue.serial)}</Alert>
                </Grid>
              }
              {siteHierarchyWarning &&
                <Grid size={{ xs: 12 }}>
                  <Alert severity="warning">{siteHierarchyWarning}</Alert>
                </Grid>
              }
              {underClientStructureWarning &&
                <Grid size={{ xs: 12 }}>
                  <Alert severity="warning">{underClientStructureWarning}</Alert>
                </Grid>
              }
              {!value.outValue?.isAccepted &&
                <Grid size={{ xs: 12 }}>
                  <Alert severity="warning">Exception: This part has not been reviewed and accepted by the Stock Controller. Please seek assistance prior to usage in this Service</Alert>
                </Grid>
              }
            </>
                }
                <Stack direction="row" justifyContent="space-between" spacing={1} marginTop={1} width="100%">
                  <Stack width="20%">
                    <Typography 
                      variant={TypographyVariantProps.Body2} 
                      fontWeight={TypographyFontWeightProps.Bold}
                    >
                In
                    </Typography>
                  </Stack>
                  <Stack direction="column" spacing={2} width="100%">
                    {showPartType && 
                <PartTypeActionSearch
                  title={showInSerialSearch ? 'Part Type Search' : 'Part/Part Type Input'}
                  serviceData={service}
                  value={value?.inValue?.name || ''} 
                  partActionType={PartActionType.REPLACE}
                  error={!!helperText.in.partTypeHelperText}
                  helperText={helperText.in.partTypeHelperText}
                  onChange={handleInPartTypeActionSearch}
                  searchType={showInSerialSearch ? 'part-type' : 'part-and-part-type'}
                  action={Action.IN}
                  IsPartCreationEnabled={showInSerialSearch ? false : true}
                  outPartTypeId={value?.outValue?.partTypeId}
                  locationId={service.subLocationNodeId || service.mainLocationNodeId}
                  serialisedOutPartId={value?.outValue?.partId}
                  nsOutPartId={value?.outValue?.ns_partId}
                />
                    }
                    {showInSerialSearch &&
                <PartTypeActionSearch
                  title="Part Serial Search"
                  serialiseType={value?.inValue?.category}
                  partTypeId={`${value?.inValue?.partTypeId}`}
                  serviceData={service}
                  value={value?.inValue?.serial || ''}
                  partActionType={PartActionType.REPLACE}
                  error={!!helperText.in.partSerialHelperText}
                  helperText={helperText.in.partSerialHelperText}
                  onChange={handleInPartTypeSerialSearch}
                  searchType="part"
                  action={Action.IN}
                />
                    }
                  </Stack>
                  {showPartType && (isInSearchMode || <ImagePopup title="Photos" images={inPartImages} />)}
                </Stack>
                {isInSearchMode || 
            <>
              {isOutSerialized ||
                <SelectButton 
                  variant={ButtonVariantProps.Secondary} 
                  color={ButtonColorProps.Success}
                  onClick={() => setIsSelectInLocationPopup(true)}
                >
                  Select Location
                </SelectButton>
              }
              {!isInSerialized &&
                <TextField 
                  label="Qty"
                  type={TextFieldTypeProps.Number}
                  value={value?.inValue?.qty}
                  inputProps={{ type: 'number', min: 0, inputMode: 'numeric', pattern: '[0-9]*' }}
                  onChange={(val) =>{
                    onChange({
                      value: value.value,
                      inValue: {
                        ...value.inValue,
                        id: value.inValue?.id || 0,
                        qty: Number(val)
                      },
                      outValue: {
                        ...value.outValue,
                        id: value.inValue?.id || 0,
                        qty: Number(val)
                      },
                    }, true);
                  }}
                />
              }
              {partTypeMismatchWarning &&
                <Grid size={{ xs: 12 }}>
                  <Alert severity="warning">{partTypeMismatchWarning}</Alert>
                </Grid>
              }
              {value?.inValue?.condition && value?.inValue?.serial && partActionConditionValidation(PartActionValidateType.REPLACEIN, value.inValue.condition, value.inValue.serial) &&
                <Grid size={{ xs: 12 }}>
                  <Alert severity="warning">{partActionConditionValidation(PartActionValidateType.REPLACEIN, value.inValue.condition, value.inValue.serial)}</Alert>
                </Grid>
              }
              {value?.inValue?.serviceId && service.id !== value?.inValue?.serviceId &&
                <Grid size={{ xs: 12 }}>
                  <Alert severity="warning">Warning: This part is marked as Reseved for Service ID {value.inValue.serviceId}.</Alert>
                </Grid>
              }
              {value?.inValue?.isInSiblingHierarchy === false && value?.outValue?.serial && partActionInValidations(value?.inValue?.condition || '', value?.inValue?.serial || '', value?.inValue?.location || '') &&
                <Grid size={{ xs: 12 }}>
                  <Alert severity="warning">{partActionInValidations(value?.inValue?.condition || '', value?.inValue?.serial || '', value?.inValue?.location || '')}</Alert>
                </Grid>
              }
              {!value.inValue?.isAccepted &&
                <Grid size={{ xs: 12 }}>
                  <Alert severity="warning">Exception: This part has not been reviewed and accepted by the Stock Controller. Please seek assistance prior to usage in this Service</Alert>
                </Grid>
              }
              <Grid size={{ xs: 12 }}>
                <PartTypeActionNote
                  value={value?.outValue?.notes || ''}
                  error={!!helperText.out.noteHelperText}
                  helperText={helperText.out.noteHelperText}
                  onChange={(note) => {
                    onChange({
                      value: value.value,
                      outValue: {
                        ...value.outValue,
                        id: value.outValue?.id || 0,
                        notes: note
                      },
                      inValue: value.inValue
                    }, true);

                    setHelperText({
                      ...helperText,
                      out: { 
                        ...helperText.out,
                        noteHelperText: ''
                      },
                    });
                  }}
                />
              </Grid>
            </>
                }
              </InnerWrapper>
            </OuterWrapper>
            :
            <Grid>
              {interceptedFormsObject?.forms?.map((form, index) =>
                <FormInterceptor
                  key={index}
                  service={service}
                  value={form}
                  onChange={(editedForms) => {
                    if (repeatableFormData.templateId === 0 && repeatableFormData.templateTitle === '') {
                      const updatedForms = interceptedFormsObject?.forms.map((formData) => formData.id === editedForms.id ? editedForms : formData);
                      setInterceptedFormsObject({
                        ...interceptedFormsObject,
                        forms: updatedForms
                      });
                    }
                  }}
                  onDamagedPartSelect={(Objectkey: string, objectValue: FormData | number | MerchantDamagePartSerialMenuData) => {
                    setMerchantDamageObjectParcer(prevState => ({ ...prevState, [Objectkey]: objectValue }));
                  }}
                  partActionData={partActionData}
                  onAddForm={(templateId, templateTitle) => setRepeatableFormData({ templateId, templateTitle })}
                  isRepeatable={getFormIsRepeatableStatus(form, interceptedFormsObject.forms)}
                  forms={interceptedFormsObject.forms}
                  isMobileView={isMobileView}
                />
              )}
              <br/>
              <Grid>
                <Stack direction="row" spacing={2} alignItems="center" ml={2}>
                  <Button
                    variant={ButtonVariantProps.Primary}
                    color={ButtonColorProps.Success}
                    onClick={submitInteractions}
                    disabled={(isInteractionSubmiting && locationInteractionSubmitting) || blockInteractionAvailable(permissions || [], interceptedFormsObject?.forms)}
                    startIcon={isInteractionSubmiting && locationInteractionSubmitting ? <Loader size={20} /> : null}
                  >
                    Submit
                  </Button>
                  {blockInteractionAvailable(permissions || [], interceptedFormsObject?.forms || []) && <Typography textColor={COLORS.Red}>{getBlockInteractionMessage(interceptedFormsObject?.forms || [])}</Typography>}
                </Stack>
              </Grid>
            </Grid>
        }
      </StyledCardContent>
      <PartLocationSelectPopup
        open={isSelectInLocationPopup}
        options={inPartLocationDataByServiceIdQuery.data || []}
        onSelect={handleInPartTypeLocationSelect}
        onClose={() => setIsSelectInLocationPopup(false)}
      />
    </DefaultCard>
  );
};